/* ── Player Tag Visual ─────────────────────────────────────── */
/* Shared presentational component for the player tag card.      */
/* Used by both the runtime HUD (HudPlayerTag) and the layout    */
/* designer (DesignerCanvas). Accepts pre-resolved data and a    */
/* pixel-based fontSize — consumers compute it from their own    */
/* sizing model.                                                  */

import { Popover, PopoverContent, PopoverTrigger, Separator } from '@workspace/app-components';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useEffect, useRef } from 'react';

/* ── Types ────────────────────────────────────────────────── */

export type PlayerTagMode = 'idle' | 'waiting' | 'active';
export type BadgeColor = 'white' | 'blue' | 'red' | 'green' | 'orange' | 'black';
export type FlashColor = 'info' | 'success' | 'warning' | 'danger';

export interface PlayerTagChip {
  label: string;
  color: string;
  /** When true, chip renders as a wider capsule showing the full label (e.g. score numbers). */
  wide?: boolean;
  /** Mark this chip as a numerical value. Enables direction-aware roll animation. */
  numerical?: boolean;
  /**
   * When true, increasing values are "good" (pulse) and decreasing are "bad" (shake).
   * When false, the opposite. Only applies when `numerical` is true.
   */
  higherIsBetter?: boolean;
  /** Human-readable description shown in the popover (e.g. "Score", "Tricks"). */
  description?: string;
}

export interface PlayerTagVisualProps {
  name: string;
  mode: PlayerTagMode;
  chips: PlayerTagChip[];
  seatLetter: string;
  seatColor: BadgeColor;
  /** Full seat name shown in the popover (e.g. "North", "East"). */
  seatName?: string;
  translucent?: boolean;
  /** Show a bot indicator next to the player name. */
  isBot?: boolean;
  /** Transient notification text (e.g. "Takes trick!"). Animates in/out automatically. Set to null to dismiss. */
  flash?: string | null;
  /** Semantic color for the flash overlay. Defaults to 'info'. */
  flashColor?: FlashColor;
  /** Base font size in pixels — all proportions derive from this. */
  fontSize: number;
  /** Class on the outermost wrapper (e.g. pointer-events control). */
  className?: string;
  /** data-player-id for test/debug targeting. */
  playerId?: string;
}

/* ── Mode-driven styles ───────────────────────────────────── */

// eslint-disable-next-line react-refresh/only-export-components
export const TAG_MODE_STYLES: Record<
  PlayerTagMode,
  {
    tagline: string;
    taglineClass: string;
    /** RGB tuple for accent color (border + shadow). */
    color: string;
    taglineAnimation: string;
  }
> = {
  idle: {
    tagline: '\u00A0',
    taglineClass: 'text-transparent',
    color: '100,116,139',
    taglineAnimation: '',
  },
  waiting: {
    tagline: 'Thinking\u2026',
    taglineClass: 'text-orange-600 font-bold',
    color: '217,119,6',
    taglineAnimation: 'animate-pulse',
  },
  active: {
    tagline: 'Your turn!',
    taglineClass: 'text-emerald-700 font-semibold',
    color: '5,150,105',
    taglineAnimation: 'animate-[flicker-3_2s_ease-in-out_infinite]',
  },
};

/* ── Seat badge colors ────────────────────────────────────── */

// eslint-disable-next-line react-refresh/only-export-components
export const BADGE_COLOR_MAP: Record<BadgeColor, { bg: string; text: string }> = {
  white: { bg: 'bg-gray-100', text: 'text-gray-800' },
  blue: { bg: 'bg-blue-500', text: 'text-white' },
  red: { bg: 'bg-red-500', text: 'text-white' },
  green: { bg: 'bg-emerald-500', text: 'text-white' },
  orange: { bg: 'bg-amber-400', text: 'text-amber-950' },
  black: { bg: 'bg-gray-900', text: 'text-white' },
};

// eslint-disable-next-line react-refresh/only-export-components
export const FLASH_COLOR_MAP: Record<FlashColor, { bg: string; border: string; text: string }> = {
  info: { bg: 'rgba(59,130,246,0.85)', border: 'rgba(59,130,246,0.9)', text: 'text-white' },
  success: { bg: 'rgba(34,197,94,0.85)', border: 'rgba(34,197,94,0.9)', text: 'text-white' },
  warning: { bg: 'rgba(234,179,8,0.88)', border: 'rgba(234,179,8,0.9)', text: 'text-white' },
  danger: { bg: 'rgba(239,68,68,0.85)', border: 'rgba(239,68,68,0.9)', text: 'text-white' },
};

/* ── Component ────────────────────────────────────────────── */

export const PlayerTagVisual = memo(function PlayerTagVisual({
  name,
  mode,
  chips,
  seatLetter,
  seatColor,
  seatName,
  translucent = true,
  isBot,
  flash,
  flashColor = 'info',
  fontSize,
  className,
  playerId,
}: PlayerTagVisualProps) {
  const modeStyle = TAG_MODE_STYLES[mode] ?? TAG_MODE_STYLES.idle;

  // Track previous chip labels per index to detect direction of numeric changes.
  const prevLabelsRef = useRef<string[]>([]);
  const chipAnimations: Array<{ direction: 'up' | 'down' | null; effect: 'pulse' | 'shake' | null }> = chips.map(
    (chip, i) => {
      if (!chip.numerical) return { direction: null, effect: null };
      const prev = prevLabelsRef.current[i];
      if (prev === undefined || prev === chip.label) return { direction: null, effect: null };
      const prevNum = Number(prev);
      const curNum = Number(chip.label);
      if (isNaN(prevNum) || isNaN(curNum)) return { direction: null, effect: null };
      const increased = curNum > prevNum;
      const isGood = chip.higherIsBetter !== false ? increased : !increased;
      return {
        direction: increased ? ('up' as const) : ('down' as const),
        effect: isGood ? ('pulse' as const) : ('shake' as const),
      };
    },
  );

  // Update ref after render (not inline) so React Strict Mode double-invocation
  // doesn't see the already-updated ref on the second call.
  const currentLabels = chips.map((c) => c.label);
  useEffect(() => {
    prevLabelsRef.current = currentLabels;
  });

  const textSize = fontSize * 0.7;
  const py = fontSize * 0.3;
  const px = fontSize * 0.7;

  const hasSeatIcon = seatLetter.length > 0;
  const hasChips = chips.length > 0;
  const { color } = modeStyle;

  // 3D shadow effect
  const sh = Math.max(1, fontSize * 0.1);
  const border = `1px solid rgba(${color},0.35)`;
  const shadow = `${sh}px ${sh}px 0px rgba(${color},0.30)`;
  const circleSize = textSize * 1.65;
  const seatIconSize = fontSize * 2;
  const seatColors = BADGE_COLOR_MAP[seatColor] ?? BADGE_COLOR_MAP.white;
  const cardBg = translucent ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,1)';
  const flashStyle = FLASH_COLOR_MAP[flashColor] ?? FLASH_COLOR_MAP.info;

  return (
    <Popover>
      <PopoverTrigger nativeButton={false} render={<div className={className} />}>
        <div
          className="relative grid cursor-pointer"
          data-player-id={playerId}
          style={{ paddingLeft: hasSeatIcon ? seatIconSize / 2 : 0, paddingRight: sh, paddingBottom: sh }}
        >
          <div
            className="rounded-sm"
            style={{
              gridArea: '1 / 1',
              background: flash ? flashStyle.bg : cardBg,
              border: flash ? `2px solid ${flashStyle.border}` : border,
              boxShadow: flash ? 'none' : shadow,
              padding: `${py}px ${px}px`,
              paddingLeft: hasSeatIcon ? `${px + seatIconSize * 0.4}px` : `${px}px`,
              paddingBottom: hasChips ? `${py + circleSize * 0.3}px` : `${py}px`,
              transition: 'background 0.25s, border 0.25s, box-shadow 0.25s',
            }}
          >
            {/* Username */}
            <div
              className={`flex items-center gap-1 text-left font-semibold whitespace-nowrap ${flash ? 'text-white' : 'text-slate-800'}`}
              style={{ fontSize: textSize, lineHeight: 1.25, maxWidth: fontSize * 6.5, transition: 'color 0.25s' }}
            >
              <span className="overflow-hidden text-ellipsis">{name}</span>
              {isBot && (
                <span
                  className={`inline-flex shrink-0 items-center justify-center rounded font-bold uppercase ${flash ? 'text-white bg-white/30 ring-1 ring-white/40' : 'text-purple-700 bg-purple-100'}`}
                  style={{
                    fontSize: textSize * 0.72,
                    lineHeight: 1,
                    height: textSize * 1.25,
                    paddingInline: textSize * 0.22,
                    transition: 'color 0.25s, background-color 0.25s',
                  }}
                >
                  bot
                </span>
              )}
            </div>

            {/* Tagline — shows flash text when active, otherwise mode tagline */}
            <div
              className={`text-left ${flash ? `font-bold ${flashStyle.text}` : `${modeStyle.taglineClass} ${modeStyle.taglineAnimation}`}`}
              style={{ fontSize: textSize, lineHeight: 1.2, marginTop: 1 }}
            >
              {flash ?? modeStyle.tagline}
            </div>
          </div>

          {/* Chip pills — circles on bottom border */}
          {hasChips && (
            <div
              className="relative flex gap-[2px]"
              style={{
                gridArea: '2 / 1',
                marginTop: -(circleSize / 2),
                marginLeft: hasSeatIcon ? px + seatIconSize * 0.25 : px,
                marginRight: px,
              }}
            >
              <AnimatePresence mode="popLayout">
                {chips.map((chip, i) => {
                  const isWide = chip.wide && chip.label.length > 1;
                  const anim = chipAnimations[i];
                  const hasDirection = !!anim?.direction;
                  const enterY = anim?.direction === 'up' ? 8 : anim?.direction === 'down' ? -8 : 0;
                  const exitY = anim?.direction === 'up' ? -8 : anim?.direction === 'down' ? 8 : 0;
                  return (
                    <motion.div
                      key={`${i}-${chip.label}`}
                      initial={hasDirection ? { y: enterY, opacity: 0 } : { scale: 0.6, opacity: 0 }}
                      animate={{
                        y: 0,
                        opacity: 1,
                        scale: anim?.effect === 'pulse' ? [1, 1.15, 1] : 1,
                        x: anim?.effect === 'shake' ? [0, -2, 2, -2, 2, 0] : 0,
                      }}
                      exit={hasDirection ? { y: exitY, opacity: 0 } : { scale: 0.6, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 20,
                        ...(anim?.effect === 'pulse' ? { scale: { duration: 0.3, ease: 'easeOut', delay: 0.05 } } : {}),
                        ...(anim?.effect === 'shake' ? { x: { duration: 0.3, ease: 'easeOut' } } : {}),
                      }}
                      className="flex items-center justify-center rounded-full text-white font-bold uppercase overflow-hidden"
                      style={{
                        height: circleSize,
                        width: isWide ? undefined : circleSize,
                        minWidth: isWide ? circleSize : undefined,
                        paddingLeft: isWide ? circleSize * 0.3 : undefined,
                        paddingRight: isWide ? circleSize * 0.3 : undefined,
                        fontSize: textSize,
                        lineHeight: 1,
                        background: chip.color,
                        border: `${Math.max(1, fontSize * 0.06)}px solid rgba(255,255,255,0.45)`,
                      }}
                    >
                      {isWide ? chip.label : chip.label.charAt(0)}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          {/* Seat letter icon — centered on left border */}
          {hasSeatIcon && (
            <div
              className={`relative flex items-center justify-center rounded-full font-bold select-none shadow-sm ${seatColors.bg} ${seatColors.text}`}
              style={{
                gridArea: '1 / 1',
                alignSelf: 'center',
                justifySelf: 'start',
                width: seatIconSize,
                height: seatIconSize,
                fontSize: seatIconSize * 0.44,
                lineHeight: 1,
                transform: 'translateX(-50%)',
                zIndex: 1,
              }}
            >
              {/* Compass watermark */}
              <svg
                className="absolute inset-0 opacity-[0.18]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: seatIconSize, height: seatIconSize }}
              >
                <circle cx="12" cy="12" r="10" />
                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
              </svg>
              <span className="relative z-10 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]">{seatLetter}</span>
            </div>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent side="top" sideOffset={8} className="w-auto min-w-48 max-w-64 p-3 text-sm" initialFocus={false}>
        {/* Header: full name + bot badge */}
        <div className="flex items-center gap-1.5 font-semibold text-foreground">
          <span className="min-w-0 break-words">{name}</span>
          {isBot && (
            <span className="shrink-0 rounded px-1 py-0.5 text-[10px] font-bold uppercase leading-none text-purple-700 bg-purple-100">
              bot
            </span>
          )}
        </div>

        {/* Divider */}
        {(hasSeatIcon || hasChips) && <Separator className="my-2" />}

        {/* Details list */}
        <div className="flex flex-col gap-1.5 text-muted-foreground">
          {hasSeatIcon && (
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${seatColors.bg} ${seatColors.text}`}
              >
                {seatLetter}
              </span>
              <span>{seatName ?? seatLetter} seat</span>
            </div>
          )}
          {chips.map((chip, i) => (
            <div key={i} className="flex items-center gap-2">
              <span
                className="inline-flex h-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                style={{ background: chip.color, minWidth: 20, paddingInline: 4 }}
              >
                {chip.label}
              </span>
              <span>{chip.description ?? chip.label}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
});
